import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '@/store/appStore'
import { upsertTeam } from '@/hooks/useTeams'
import { addSeason } from '@/hooks/useSeasons'
import { sportProfiles } from '@/sports'
import { ChevronRight, ArrowLeft, Check, Users, CalendarDays, Flag, X } from 'lucide-react'
import type { Team } from '@/types'

const AGE_GROUPS = ['Varsity', 'JV', 'Freshman', 'Middle School', 'U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Club', 'College', 'Adult Recreation']

type Step = 'welcome' | 'team' | 'season' | 'done'

const STEPS: Step[] = ['welcome', 'team', 'season', 'done']

function ProgressDots({ current }: { current: Step }) {
  const idx = STEPS.indexOf(current)
  const dots = STEPS.slice(1) // welcome is pre-progress
  const dotIdx = idx - 1
  if (idx === 0) return null
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {dots.map((_, i) => (
        <div
          key={i}
          className={`rounded-full transition-all duration-300 ${
            i < dotIdx ? 'w-2 h-2 bg-[#2563eb]' :
            i === dotIdx ? 'w-6 h-2 bg-[#2563eb]' :
            'w-2 h-2 bg-[#e2e8f0] dark:bg-[#334155]'
          }`}
        />
      ))}
    </div>
  )
}

interface Props {
  canCancel?: boolean   // true when adding a 2nd+ team (already has one)
  onDone?: () => void   // called after wizard completes or is cancelled
}

export default function SetupWizard({ canCancel = false, onDone }: Props) {
  const navigate = useNavigate()
  const { setActiveTeam, setActiveSeason } = useAppStore()

  const [step, setStep] = useState<Step>('welcome')
  const [saving, setSaving] = useState(false)

  // Team fields
  const [teamName, setTeamName] = useState('')
  const [school, setSchool] = useState('')
  const [ageGroup, setAgeGroup] = useState('')
  const [sportProfileId, setSportProfileId] = useState('')

  // Season fields
  const currentYear = new Date().getFullYear()
  const [seasonName, setSeasonName] = useState(`${currentYear} Season`)
  const [seasonYear, setSeasonYear] = useState(String(currentYear))

  // Saved team (needed to create season)
  const [savedTeam, setSavedTeam] = useState<Team | null>(null)

  async function handleSaveTeam() {
    if (!teamName.trim() || !sportProfileId) return
    setSaving(true)
    try {
      const now = new Date().toISOString()
      const team: Team = {
        id: crypto.randomUUID(),
        name: teamName.trim(),
        school: school.trim() || undefined,
        ageGroup: ageGroup || undefined,
        sportProfileId,
        createdAt: now,
        updatedAt: now,
      }
      await upsertTeam(team)
      setActiveTeam(team)
      setSavedTeam(team)
      setStep('season')
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveSeason() {
    if (!savedTeam) return
    setSaving(true)
    try {
      const season = await addSeason({
        teamId: savedTeam.id,
        name: seasonName.trim() || `${currentYear} Season`,
        year: parseInt(seasonYear) || currentYear,
        isActive: true,
      })
      setActiveSeason(season)
    } finally {
      setSaving(false)
      setStep('done')
    }
  }

  async function handleSkipSeason() {
    setStep('done')
  }

  // ── Welcome ───────────────────────────────────────────────────────────────
  if (step === 'welcome') {
    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-[#0f172a] flex flex-col items-center justify-center p-6 text-center">
        {canCancel && (
          <button onClick={onDone} className="absolute top-12 right-6 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-[#94a3b8] hover:text-[#64748b]"><X className="w-5 h-5" /></button>
        )}
        <div className="w-20 h-20 bg-[#2563eb] rounded-3xl flex items-center justify-center mb-6 shadow-lg">
          <Flag className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-[#0f172a] dark:text-white mb-3">{canCancel ? 'New Team' : 'Welcome to Whistle'}</h1>
        <p className="text-[#64748b] dark:text-[#94a3b8] max-w-xs mb-10">
          {canCancel ? 'Set up another team in your account.' : "Your coaching command center. Let's get your team set up — takes about 2 minutes."}
        </p>
        <button
          onClick={() => setStep('team')}
          className="w-full max-w-xs py-4 bg-[#2563eb] text-white rounded-2xl font-bold text-base flex items-center justify-center gap-2 shadow-sm"
        >
          Get Started <ChevronRight className="w-5 h-5" />
        </button>
        <p className="text-xs text-[#94a3b8] mt-4">No credit card required</p>
      </div>
    )
  }

  // ── Team Setup ────────────────────────────────────────────────────────────
  if (step === 'team') {
    const canContinue = teamName.trim().length > 0 && sportProfileId.length > 0
    const sportsByCategory = Object.entries(
      sportProfiles.reduce<Record<string, typeof sportProfiles>>((acc, p) => {
        if (!acc[p.sport]) acc[p.sport] = []
        acc[p.sport].push(p)
        return acc
      }, {})
    )

    return (
      <div className="fixed inset-0 z-50 bg-[#e2e8f0] dark:bg-[#0f172a] overflow-y-auto">
        <div className="min-h-screen flex flex-col p-4 pt-safe">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6 mt-2">
            <button
              onClick={() => setStep('welcome')}
              className="w-10 h-10 flex items-center justify-center rounded-xl text-[#64748b] dark:text-[#94a3b8] bg-white dark:bg-[#1e293b]"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <ProgressDots current="team" />
            </div>
            {canCancel && (
              <button onClick={onDone} className="w-10 h-10 flex items-center justify-center rounded-xl text-[#94a3b8] bg-white dark:bg-[#1e293b]">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          <h2 className="text-2xl font-bold text-[#0f172a] dark:text-white mb-1">Your team</h2>
          <p className="text-sm text-[#64748b] dark:text-[#94a3b8] mb-6">Tell us a bit about your squad</p>

          {/* Team name */}
          <div className="bg-white dark:bg-[#1e293b] rounded-2xl p-4 mb-3 shadow-sm">
            <label className="block text-xs font-medium text-[#64748b] dark:text-[#94a3b8] mb-1">Team name *</label>
            <input
              autoFocus
              value={teamName}
              onChange={e => setTeamName(e.target.value)}
              placeholder="e.g. Westfield Lacrosse"
              className="w-full text-base font-semibold bg-transparent text-[#0f172a] dark:text-white placeholder:text-[#cbd5e1] dark:placeholder:text-[#475569] outline-none"
            />
          </div>

          {/* School + Age group */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-white dark:bg-[#1e293b] rounded-2xl p-4 shadow-sm">
              <label className="block text-xs font-medium text-[#64748b] dark:text-[#94a3b8] mb-1">School</label>
              <input
                value={school}
                onChange={e => setSchool(e.target.value)}
                placeholder="Optional"
                className="w-full text-sm bg-transparent text-[#0f172a] dark:text-white placeholder:text-[#cbd5e1] dark:placeholder:text-[#475569] outline-none"
              />
            </div>
            <div className="bg-white dark:bg-[#1e293b] rounded-2xl p-4 shadow-sm">
              <label className="block text-xs font-medium text-[#64748b] dark:text-[#94a3b8] mb-1">Level</label>
              <select
                value={ageGroup}
                onChange={e => setAgeGroup(e.target.value)}
                className="w-full text-sm bg-transparent text-[#0f172a] dark:text-white outline-none"
              >
                <option value="">Optional</option>
                {AGE_GROUPS.map(ag => <option key={ag} value={ag}>{ag}</option>)}
              </select>
            </div>
          </div>

          {/* Sport picker */}
          <div className="bg-white dark:bg-[#1e293b] rounded-2xl p-4 mb-6 shadow-sm">
            <label className="block text-xs font-medium text-[#64748b] dark:text-[#94a3b8] mb-3">Sport *</label>
            {sportsByCategory.map(([category, profiles]) => (
              <div key={category} className="mb-3 last:mb-0">
                <p className="text-[10px] uppercase tracking-wider text-[#94a3b8] mb-2">{category}</p>
                <div className="grid grid-cols-1 gap-2">
                  {profiles.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSportProfileId(p.id)}
                      className={`flex items-center gap-3 px-3 py-3 rounded-xl border-2 transition-colors text-left ${
                        sportProfileId === p.id
                          ? 'border-[#2563eb] bg-[#eff6ff] dark:bg-[#1e3a6b]'
                          : 'border-[#e2e8f0] dark:border-[#334155] bg-[#f8fafc] dark:bg-[#0f172a]'
                      }`}
                    >
                      <span className="text-2xl">{p.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${sportProfileId === p.id ? 'text-[#2563eb]' : 'text-[#0f172a] dark:text-white'}`}>
                          {p.label}
                        </p>
                        <p className="text-xs text-[#64748b] dark:text-[#94a3b8] truncate">
                          {p.periods.count}×{p.periods.durationMinutes}min {p.periods.label.toLowerCase()}s · {p.playersOnField} on field
                        </p>
                      </div>
                      {sportProfileId === p.id && (
                        <div className="w-5 h-5 bg-[#2563eb] rounded-full flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3 text-white" strokeWidth={3} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={handleSaveTeam}
            disabled={!canContinue || saving}
            className="w-full py-4 bg-[#2563eb] text-white rounded-2xl font-bold text-base flex items-center justify-center gap-2 disabled:opacity-40 shadow-sm mb-8"
          >
            {saving ? 'Saving…' : <>Continue <ChevronRight className="w-5 h-5" /></>}
          </button>
        </div>
      </div>
    )
  }

  // ── Season ────────────────────────────────────────────────────────────────
  if (step === 'season') {
    return (
      <div className="fixed inset-0 z-50 bg-[#e2e8f0] dark:bg-[#0f172a] flex flex-col">
        <div className="flex-1 flex flex-col p-4 pt-safe">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6 mt-2">
            <div className="w-10 h-10" />
            <div className="flex-1">
              <ProgressDots current="season" />
            </div>
          </div>

          <div className="flex-1 flex flex-col justify-center">
            <div className="w-14 h-14 bg-[#dbeafe] dark:bg-[#1e3a6b] rounded-2xl flex items-center justify-center mb-4">
              <CalendarDays className="w-7 h-7 text-[#2563eb]" />
            </div>
            <h2 className="text-2xl font-bold text-[#0f172a] dark:text-white mb-1">First season</h2>
            <p className="text-sm text-[#64748b] dark:text-[#94a3b8] mb-8">
              Create a season to organize your games and stats.
            </p>

            <div className="bg-white dark:bg-[#1e293b] rounded-2xl p-4 mb-3 shadow-sm space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#64748b] dark:text-[#94a3b8] mb-1">Season name</label>
                <input
                  value={seasonName}
                  onChange={e => setSeasonName(e.target.value)}
                  className="w-full text-base font-semibold bg-transparent text-[#0f172a] dark:text-white placeholder:text-[#cbd5e1] outline-none border-b border-[#e2e8f0] dark:border-[#334155] pb-1"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#64748b] dark:text-[#94a3b8] mb-1">Year</label>
                <input
                  type="number"
                  value={seasonYear}
                  onChange={e => setSeasonYear(e.target.value)}
                  className="w-full text-sm bg-transparent text-[#0f172a] dark:text-white outline-none"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3 pb-8">
            <button
              onClick={handleSaveSeason}
              disabled={saving}
              className="w-full py-4 bg-[#2563eb] text-white rounded-2xl font-bold text-base flex items-center justify-center gap-2 disabled:opacity-40 shadow-sm"
            >
              {saving ? 'Creating…' : <>Create Season <ChevronRight className="w-5 h-5" /></>}
            </button>
            <button
              onClick={handleSkipSeason}
              className="w-full py-3 text-[#64748b] dark:text-[#94a3b8] text-sm font-medium"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Done ──────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 bg-white dark:bg-[#0f172a] flex flex-col items-center justify-center p-6">
      <div className="w-20 h-20 bg-[#22c55e] rounded-3xl flex items-center justify-center mb-6 shadow-lg">
        <Check className="w-10 h-10 text-white" strokeWidth={3} />
      </div>
      <h2 className="text-3xl font-bold text-[#0f172a] dark:text-white mb-2">You're all set!</h2>
      <p className="text-[#64748b] dark:text-[#94a3b8] text-center max-w-xs mb-10">
        Your team is ready. Here's what to do next.
      </p>

      <div className="w-full max-w-xs space-y-3 mb-6">
        <button
          onClick={() => { onDone?.(); navigate('/roster') }}
          className="w-full bg-white dark:bg-[#1e293b] border border-[#e2e8f0] dark:border-[#334155] rounded-2xl p-4 flex items-center gap-4 shadow-sm text-left"
        >
          <div className="w-10 h-10 bg-[#dbeafe] dark:bg-[#1e3a6b] rounded-xl flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-[#2563eb]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-[#0f172a] dark:text-white">Add players</p>
            <p className="text-xs text-[#64748b] dark:text-[#94a3b8]">Build your roster</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#94a3b8] shrink-0" />
        </button>

        <button
          onClick={() => { onDone?.(); navigate('/schedule') }}
          className="w-full bg-white dark:bg-[#1e293b] border border-[#e2e8f0] dark:border-[#334155] rounded-2xl p-4 flex items-center gap-4 shadow-sm text-left"
        >
          <div className="w-10 h-10 bg-[#dcfce7] dark:bg-[#14532d] rounded-xl flex items-center justify-center shrink-0">
            <CalendarDays className="w-5 h-5 text-[#16a34a]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-[#0f172a] dark:text-white">Add games</p>
            <p className="text-xs text-[#64748b] dark:text-[#94a3b8]">Schedule your season</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#94a3b8] shrink-0" />
        </button>
      </div>

      <button
        onClick={() => { onDone?.(); navigate('/') }}
        className="text-sm text-[#64748b] dark:text-[#94a3b8] font-medium"
      >
        Go to Dashboard
      </button>
    </div>
  )
}
